import { type FC } from 'react';
import { Popconfirm } from 'antd';
import { isBoolean } from 'lodash';
import { observer } from 'mobx-react-lite';
import { Space, Button } from '@g2u/ui-kit';
import { useNavigate } from 'react-router';

import { type RescheduleReason, ReschedulingSteps, type WithOptionalDataTest } from 'types';
import { BookingStore } from 'store';

type Props = {
  selectedReason?: RescheduleReason;
  disabled: boolean;
  step: ReschedulingSteps;
  jobId: string;
  isLoading?: boolean;
  onReschedule: () => void;
  onNotify: () => void;
  onUpdate: () => void;
} & WithOptionalDataTest;

export const RescheduleActions: FC<Props> = observer((props) => {
  const { resetBookingProgress, isNotified } = BookingStore;
  const { selectedReason, step, isLoading, disabled, jobId, onReschedule, onNotify, onUpdate } = props;
  const navigate = useNavigate();

  const onBack = () => navigate(-1);
  const onCancel = () => navigate(isBoolean(isNotified) ? `/booking/confirmation/${jobId}` : '/job/search');
  const onSkip = () => navigate(`/job/reschedule/${jobId}/${ReschedulingSteps.ReviewSummary}`);
  const onNext = (step: ReschedulingSteps) => {
    switch (step) {
      case ReschedulingSteps.SelectSlot:
      case ReschedulingSteps.SelectSlotSimple:
        return onReschedule();
      case ReschedulingSteps.SendNotification:
        return onNotify();
      case ReschedulingSteps.EditDetails:
        return onUpdate();
      default:
        return navigate(`/job/reschedule/${jobId}/${nextStep(step)}`);
    }
  };

  const showBackButton = ![
    ReschedulingSteps.SelectReason,
    ReschedulingSteps.SendNotification,
    ReschedulingSteps.ReviewSummary,
  ].includes(step);
  const showSkipButton = step === ReschedulingSteps.SendNotification;
  const showCancelButton = ![ReschedulingSteps.SendNotification, ReschedulingSteps.ReviewSummary].includes(step);
  const showNextButton = step !== ReschedulingSteps.ReviewDetails;

  const nextStep = (currentStep: ReschedulingSteps) => {
    switch (currentStep) {
      case ReschedulingSteps.SelectReason:
        return selectedReason?.category === 'R' ? ReschedulingSteps.SelectSlotSimple : ReschedulingSteps.ReviewDetails;
      case ReschedulingSteps.SelectSlot:
        return ReschedulingSteps.SendNotification;
      case ReschedulingSteps.SendNotification:
        return ReschedulingSteps.ReviewSummary;
      case ReschedulingSteps.ReviewSummary:
        return ReschedulingSteps.ReviewSummary;
      default:
        return ReschedulingSteps.SelectReason;
    }
  };

  const nextButtonText = (step: ReschedulingSteps) => {
    switch (step) {
      case ReschedulingSteps.SelectReason:
        return 'Confirm >';
      case ReschedulingSteps.EditDetails:
        return 'Next (Select technician(s) & time) >';
      case ReschedulingSteps.SelectSlot:
        return 'Confirm Technician and Time >';
      case ReschedulingSteps.SendNotification:
        return 'Send Notifications';
      case ReschedulingSteps.ReviewSummary:
        return 'Reschedule completed! Return to the Dashboard.';
      default:
        return 'Confirm >';
    }
  };

  return (
    <Space.$
      justify={step === ReschedulingSteps.ReviewSummary ? Space.Justify.Center : Space.Justify.End}
      size={Space.Size.Middle}
    >
      {showBackButton && (
        <Button.$ dataTestId="back-btn" onClick={onBack} disabled={isLoading}>
          {'< Back'}
        </Button.$>
      )}
      {showCancelButton && (
        <Popconfirm
          title="Cancel the Rescheduling"
          description="Are you sure to cancel this rescheduling?"
          okText="Yes"
          onConfirm={onCancel}
        >
          <div>
            <Button.$ danger dataTestId="cancel-rescheduling-btn" disabled={isLoading}>
              Cancel the rescheduling
            </Button.$>
          </div>
        </Popconfirm>
      )}
      {showSkipButton && (
        <Button.$ dataTestId="skip-btn" onClick={onSkip} disabled={isLoading} loading={isLoading}>
          Skip Sending Notification
        </Button.$>
      )}
      {showNextButton && (
        <Button.$
          type={Button.Type.Primary}
          dataTestId="reschedule-btn"
          onClick={() =>
            step === ReschedulingSteps.ReviewSummary ? (resetBookingProgress(), navigate('/dashboard')) : onNext(step)
          }
          disabled={disabled}
          loading={isLoading}
        >
          <>{nextButtonText(step)}</>
        </Button.$>
      )}
    </Space.$>
  );
});
